import Stack from '@mui/material/Stack';
import { Typography, useTheme } from '@mui/material';
import { renderPrice } from '../../assets/helpers/utils/index.ts';
import { Button } from '../../components/button/index.ts';
import { ArrowNextIcon } from '../../assets/icons/arrow-next.tsx';
import Box from '@mui/material/Box';
import { CustomLineChart } from '../line-chart/index.ts';
import { Card } from '../../components/index.ts';
import * as React from 'react';
import { memo, useMemo } from 'react';
import type { ChartData } from '../../api/types.ts';

type PT = {
  revenue: number;
  gross_profit: number;
  entity_profit: number;
  entity_percentage: number;
  gradientId: number | string;
  revenueChartData?: ChartData[];
  grossProfitChartData?: ChartData[];
};

const pad2 = (n: number) => String(n).padStart(2, '0');

const formatIsoDate = (date: Date) => {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const createSkeletonPair = (): {
  revenue: ChartData[];
  grossProfit: ChartData[];
} => {
  const revenueTemplate = [18, 17, 16, 15, 14, 14, 14, 14, 15, 17, 19, 19];
  const grossTemplate = [16, 15, 14, 13, 12, 12, 12, 12, 13, 15, 17, 17];

  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  startDate.setDate(startDate.getDate() - revenueTemplate.length + 1);

  const randomShift = () => Math.round((Math.random() - 0.5) * 2);

  const dates = revenueTemplate.map((_, index) => formatIsoDate(addDays(startDate, index)));

  return {
    revenue: revenueTemplate.map((value, index) => ({
      date: dates[index],
      value: Math.max(1, value + randomShift()),
    })),
    grossProfit: grossTemplate.map((value, index) => ({
      date: dates[index],
      value: Math.max(1, value + randomShift()),
    })),
  };
};

export const RevenueAndGrossProfitChart = memo((props: PT) => {
  const {
    entity_profit,
    entity_percentage,
    gross_profit,
    revenue,
    revenueChartData,
    grossProfitChartData,
    gradientId,
  } = props;

  const { palette } = useTheme();

  const isEmptyData = !revenueChartData?.length && !grossProfitChartData?.length;

  const skeletonPair = useMemo(() => createSkeletonPair(), []);

  const displayRevenueChartData = isEmptyData ? skeletonPair.revenue : revenueChartData;
  const displayGrossProfitChartData = isEmptyData ? skeletonPair.grossProfit : grossProfitChartData;

  return (
    <Card sx={{ p: 6, height: 348, borderRadius: 6 }}>
      <Stack direction={'row'} height={'100%'} gap={12}>
        <Stack width={231} gap={6}>
          <Stack>
            <Typography
              variant={'title'}
              color={palette.semi_dark}
              sx={{
                position: 'relative',
                '::after': {
                  position: 'absolute',
                  bottom: '2px',
                  content: '""',
                  display: 'block',
                  width: '72px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: isEmptyData ? palette.light_dark : palette.accent,
                },
              }}
            >
              Revenue
            </Typography>

            <Typography variant={'header'} color={isEmptyData ? palette.bgDark : palette.dark}>
              {isEmptyData ? '—' : renderPrice(revenue)}
            </Typography>
          </Stack>

          <Stack>
            <Typography
              variant={'title'}
              color={palette.semi_dark}
              sx={{
                position: 'relative',
                '::after': {
                  position: 'absolute',
                  bottom: '2px',
                  content: '""',
                  display: 'block',
                  width: '96px',
                  borderBottom: '2px dashed',
                  borderColor: isEmptyData ? palette.light_dark : palette.accent_text,
                },
              }}
            >
              Gross profit
            </Typography>

            <Typography variant={'header'} color={isEmptyData ? palette.bgDark : palette.dark}>
              {isEmptyData ? '—' : renderPrice(gross_profit)}
            </Typography>

            <Typography variant={'body_14_24'} color={palette.semi_dark}>
              Club profit: {isEmptyData ? '-' : `${renderPrice(entity_profit)}・${entity_percentage}%`}
            </Typography>
          </Stack>

          <Button variantStyle={'regular'} sx={{ marginTop: 'auto' }} disabled={isEmptyData}>
            View advanced analytics
            <ArrowNextIcon />
          </Button>
        </Stack>

        <Box
          sx={{
            minWidth: 0,
            flexGrow: 1,
            height: '100%',
            position: 'relative',
          }}
        >
          <CustomLineChart
            data={displayRevenueChartData}
            skeletonMode={isEmptyData}
            margin={{ bottom: 0 }}
            tooltipFormatter={({ value, timeLabel }) => `${renderPrice(value)} ${timeLabel}`}
            seriesValueFormatter={(value) => renderPrice(value)}
            yAxisValueFormatter={(value) => renderPrice(value)}
            gradientProps={{
              gradientId: `ElGradient${gradientId}`,
              ...(isEmptyData && {
                firstStopColor: palette.light_dark,
                firstStopOpacity: '0.18',
                secondStopColor: palette.light_dark,
                secondStopOpacity: '0',
              }),
            }}
            extraSeries={[
              {
                id: 'secondary',
                data: displayGrossProfitChartData,
                showMark: false,
                color: isEmptyData ? palette.light_dark : palette.accent_text,
                area: false,
              },
            ]}
            sx={{
              '& .MuiChartsAxis-directionX': {
                display: 'flex',
              },

              '& .MuiLineElement-root[data-series="secondary"]': {
                strokeDasharray: '6 4',
                strokeWidth: 1,
                stroke: isEmptyData ? palette.light_dark : palette.accent_text,
                opacity: isEmptyData ? 0.9 : 1,
              },

              '& .MuiLineElement-root[data-series="main"]': {
                strokeWidth: 1,
                opacity: isEmptyData ? 0.9 : 1,
              },
            }}
          />

          <Typography
            variant={'body_14_24'}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              color: isEmptyData ? palette.light_dark : palette.accent_text,
            }}
          >
            {isEmptyData ? 'No data available' : 'Last 30 day'}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
});
